import React, { memo, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import DropdownMC from '../../components/DropdownMC';
import MultiSelectDropdownMC from '../../components/MultiSelectDropdownMC';
import SearchMC from '../../components/SearchMC';
import DateRangeMC from '../../components/DateRangeMC';
import MultiSelectDropdownGroupMC from '../../components/MultiSelectDropdownGroupMC';

/**
 * InboxFilterComponentMC - Componente Atómico (Molecule)
 * 
 * Renderiza componentes de filtros específicamente optimizados para Inbox
 * Sigue principios de Atomic Design y optimización de renderizado
 * 
 * CARACTERÍSTICAS:
 * - Componente puramente presentacional
 * - Memoización completa con React.memo
 * - Callbacks memoizados para evitar re-renders
 * - Adaptación automática de estructuras de datos
 * - Compatibilidad con todos los tipos de filtros
 * 
 * OPTIMIZACIONES:
 * - useMemo para transformaciones de datos
 * - useCallback para handlers
 * - Props validation con PropTypes
 * - Estructura de datos adaptada automáticamente
 * 
 * @param {Object} filter - Configuración del filtro
 * @param {string} filter.type - Tipo de filtro: 'search', 'select', 'multi-select', 'date-range'
 * @param {string} filter.key - Clave única del filtro
 * @param {Array} filter.options - Opciones disponibles para selects
 * @param {string} filter.placeholder - Texto placeholder
 * @param {string} filter.label - Etiqueta del filtro
 * @param {boolean} filter.dependencyKey - Si depende de otro filtro
 * @param {string} filter.containerClassName - Clases CSS adicionales
 * @param {string|number} componentKey - Key único para React
 * @param {Object} activeFilters - Filtros actualmente activos
 * @param {function} onFilterChange - Callback para cambios en filtros
 */
const InboxFilterComponentMC = memo(({
  filter,
  componentKey,
  activeFilters,
  onFilterChange
}) => {
  // Memoizar estado de habilitado/deshabilitado
  const isDisabled = useMemo(() => {
    return filter?.dependencyKey && (!filter?.options || filter?.options.length === 0);
  }, [filter?.dependencyKey, filter?.options]);

  // Memoizar función de formateo para multi-select
  const formatMultiIds = useCallback((param) => {
    if (param == null) return [];
    if (Array.isArray(param)) return param;
    if (typeof param === 'string') {
      return param
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s !== '');
    }
    return [];
  }, []);

  // Memoizar handler de cambios genérico
  const handleChange = useCallback((value) => {
    onFilterChange?.({
      filterKey: filter?.key,
      filterValue: value,
      filterType: filter?.type
    });
  }, [filter?.key, filter?.type, onFilterChange]);

  // Memoizar handler específico para multi-select
  const handleMultiSelectChange = useCallback((_, selectedArray) => {
    onFilterChange?.({
      filterKey: filter?.key,
      filterValue: selectedArray,
      filterType: filter?.type
    });
  }, [filter?.key, filter?.type, onFilterChange]);

  // Memoizar opciones adaptadas para MultiSelectDropdownMC
  const adaptedOptions = useMemo(() => {
    if (filter?.type !== 'multi-select') return filter?.options || [];
    
    return (filter?.options || []).map(option => ({
      id: option.value,
      label: option.label,
      color: option.color,
      ...option
    }));
  }, [filter?.type, filter?.options]);

  // Memoizar valores por defecto formateados
  const defaultValue = useMemo(() => {
    if (!filter || !activeFilters) return undefined;
    const currentValue = activeFilters[filter.key];
    if (filter.type === 'multi-select') {
      return formatMultiIds(currentValue);
    }
    if (filter.type === 'multi-select-group') {
      if (Array.isArray(currentValue) && currentValue.every(Array.isArray)) {
        return currentValue.map(arr => [...arr]);
      }
      if (Array.isArray(currentValue)) {
        return [currentValue];
      }
      return [[]];
    }
    if (filter.type === 'date-range') {
      return currentValue || {};
    }
    if (filter.type === 'select') {
      return currentValue && currentValue !== '' ? currentValue : null;
    }
    return currentValue;
  }, [activeFilters, filter, formatMultiIds]);

  // Renderizar componente según el tipo
  const renderFilterComponent = useMemo(() => {
    if (!filter) return null;
    switch (filter.type) {
      case 'search':
        return (
          <SearchMC
            key={componentKey}
            onChange={handleChange}
            placeholder={filter.placeholder || filter.label}
            defaultValue={defaultValue}
            mainContainerClassName="tw-max-w-96"
            debounceTime={500}
          />
        );

      case 'select':
        return (
          <DropdownMC
            options={filter.options || []}
            key={componentKey}
            label={filter.label}
            onSelect={handleChange}
            defaultValue={defaultValue}
            disabled={isDisabled}
            placeholder={filter.placeholder}
            labelClassName="!tw-text-gray-400 tw-text-default/[26px]"
          />
        );

      case 'multi-select':
        return (
          <MultiSelectDropdownMC
            search
            id={filter.key}
            name={filter.key}
            options={adaptedOptions}
            defaultValue={defaultValue}
            onSelect={handleMultiSelectChange}
            placeholder={filter.placeholder || ''}
            key={componentKey}
          />
        );

      case 'date-range':
        return (
          <DateRangeMC
            key={componentKey}
            handleChange={handleChange}
            defaultDate={defaultValue}
            label={filter.label}
            placeholder={filter.placeholder}
          />
        );
      
      case 'multi-select-group':
        return (
          <MultiSelectDropdownGroupMC
            search
            id={filter.key}
            key={componentKey}
            name={filter.key}
            options={adaptedOptions}
            defaultValue={defaultValue}
            onChange={handleMultiSelectChange}
            addButtonLabel={filter.addButtonLabel || ''}
            placeholder={filter.placeholder || ''}
          />
        );

      default:
        return null;
    }
  }, [
    filter,
    componentKey,
    defaultValue,
    adaptedOptions,
    isDisabled,
    handleChange,
    handleMultiSelectChange
  ]);

  // Validación de props requeridas (después de todos los hooks)
  if (!filter || !activeFilters || !onFilterChange) {
    return null;
  }

  return (
    <div 
      className={`tw-flex tw-flex-col tw-align-middle tw-justify-center ${filter.containerClassName || ''}`}
    >
      {renderFilterComponent}
    </div>
  );
});

// Display name para debugging
InboxFilterComponentMC.displayName = 'InboxFilterComponentMC';

// PropTypes para validación en desarrollo
InboxFilterComponentMC.propTypes = {
  filter: PropTypes.shape({
    type: PropTypes.oneOf(['search', 'select', 'multi-select', 'date-range', 'multi-select-group']).isRequired,
    key: PropTypes.string.isRequired,
    options: PropTypes.array,
    placeholder: PropTypes.string,
    label: PropTypes.string,
    dependencyKey: PropTypes.string,
    containerClassName: PropTypes.string
  }).isRequired,
  componentKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  activeFilters: PropTypes.object.isRequired,
  onFilterChange: PropTypes.func.isRequired
};

export default InboxFilterComponentMC;